import { useState, useRef, useEffect } from 'react';
import { Button, useTheme, spacing } from '@fti/design-system';

interface ExportDropdownProps {
  loading?: boolean;
  onExport: (format: 'xlsx' | 'parquet') => void;
}

export function ExportDropdown({ loading, onExport }: ExportDropdownProps) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (format: 'xlsx' | 'parquet') => {
    setOpen(false);
    onExport(format);
  };

  const itemStyle = {
    display: 'block',
    width: '100%',
    padding: `${spacing.xs} ${spacing.sm}`,
    border: 'none',
    background: 'none',
    textAlign: 'left' as const,
    fontSize: '0.85rem',
    cursor: 'pointer',
    color: theme.colors.text.primary,
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        disabled={loading}
        loading={loading}
      >
        Export
      </Button>
      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 4,
            background: theme.colors.background.elevated,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            zIndex: 10,
            minWidth: 140,
            overflow: 'hidden',
          }}
        >
          <button
            style={itemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = theme.colors.background.paper)}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            onClick={() => handleSelect('xlsx')}
          >
            Excel (.xlsx)
          </button>
          <button
            style={itemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = theme.colors.background.paper)}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            onClick={() => handleSelect('parquet')}
          >
            Parquet (.parquet)
          </button>
        </div>
      )}
    </div>
  );
}
